
import Vue from 'vue'
import { URL_SYSTEM } from '@/constants/urls.js'
import { SYSTEM_SETTINGS, ERRORS, AXIOS_REQUESTS } from '@/constants/store.js'

const state = {
  [SYSTEM_SETTINGS.STATE.IS_TEST_MODE]: false,
  [SYSTEM_SETTINGS.STATE.SERVER_TIME]: null,
  [SYSTEM_SETTINGS.STATE.LOCALIZATION_ENABLED]: false,
  [SYSTEM_SETTINGS.STATE.VERSIONS]: null,
  [SYSTEM_SETTINGS.STATE.IS_LOADED]: false,
  [SYSTEM_SETTINGS.STATE.SMTP_SETTINGS]: null,
  [SYSTEM_SETTINGS.STATE.CONFIGURATION]: {
    loaded: false,
    themeProps: null,
    segment: null
  }
}

const getters = {
  [SYSTEM_SETTINGS.GETTERS.SERVER_TIME]: (state) => {
    return state.serverTime
      ? Vue.moment(state.serverTime)
      : Vue.moment()
  }
}

const actions = {
  async [SYSTEM_SETTINGS.ACTIONS.GET_CONFIGURATION] ({ dispatch, commit }) {
    const model = {
      url: `${Vue.getBusinessHost()}${URL_SYSTEM}/config?${Vue.helpers.getQueryParamsString()}`
    }
    const resp = await dispatch(AXIOS_REQUESTS.ACTIONS.GET_AXIOS_REQUEST, model, { root: true })
    commit(ERRORS.MUTATIONS.ERROR_CLEAR, null, { root: true })
    commit(SYSTEM_SETTINGS.MUTATIONS.GET_CONFIGURATION, resp.data)
  },
  async [SYSTEM_SETTINGS.ACTIONS.EDIT_SEGMENT_CONFIGURATION] ({ dispatch, commit }, model) {
    model = {
      url: `${Vue.getBusinessHost()}${URL_SYSTEM}/segmentConfigs`,
      model
    }
    await dispatch(AXIOS_REQUESTS.ACTIONS.PUT_AXIOS_REQUEST, model, { root: true })
    commit(ERRORS.MUTATIONS.ERROR_CLEAR, null, { root: true })
  },
  async [SYSTEM_SETTINGS.ACTIONS.EDIT_CLIENT_THEME] ({ dispatch, commit }, model) {
    model = {
      url: `${Vue.getBusinessHost()}${URL_SYSTEM}/clientTheme/${model.themeType}`,
      model: model.props
    }
    await dispatch(AXIOS_REQUESTS.ACTIONS.PUT_AXIOS_REQUEST, model, { root: true })
    commit(ERRORS.MUTATIONS.ERROR_CLEAR, null, { root: true })
  },
  async [SYSTEM_SETTINGS.ACTIONS.RESET_TO_DEFAULT_THEME] ({ dispatch, commit }) {
    const model = {
      url: `${Vue.getBusinessHost()}${URL_SYSTEM}/clientTheme/default`,
      model: null
    }
    await dispatch(AXIOS_REQUESTS.ACTIONS.PUT_AXIOS_REQUEST, model, { root: true })
    commit(ERRORS.MUTATIONS.ERROR_CLEAR, null, { root: true })
  },
  async [SYSTEM_SETTINGS.ACTIONS.GET_SMTP_SETTINGS] ({ dispatch, commit }) {
    const model = {
      url: `${Vue.getBusinessHost()}${URL_SYSTEM}/smtp?${Vue.helpers.getQueryParamsString()}`
    }
    const resp = await dispatch(AXIOS_REQUESTS.ACTIONS.GET_AXIOS_REQUEST, model, { root: true })
    commit(ERRORS.MUTATIONS.ERROR_CLEAR, null, { root: true })
    commit(SYSTEM_SETTINGS.MUTATIONS.GET_SMTP_SETTING, resp.data)
  },
  async [SYSTEM_SETTINGS.ACTIONS.EDIT_SMTP_SETTINGS] ({ dispatch, commit }, model) {
    model = {
      url: `${Vue.getBusinessHost()}${URL_SYSTEM}/smtp`,
      model
    }
    await dispatch(AXIOS_REQUESTS.ACTIONS.PUT_AXIOS_REQUEST, model, { root: true })
    commit(ERRORS.MUTATIONS.ERROR_CLEAR, null, { root: true })
  },

  async [SYSTEM_SETTINGS.ACTIONS.CHANGE_TEST_STATUS] ({ dispatch, commit }, testEnvEnabled) {
    const model = {
      url: `${Vue.getAdminHost()}${URL_SYSTEM}/testStatus`,
      model: { testEnvEnabled }
    }
    await dispatch(AXIOS_REQUESTS.ACTIONS.PUT_AXIOS_REQUEST, model, { root: true })
    commit(ERRORS.MUTATIONS.ERROR_CLEAR, null, { root: true })
    Vue.changeTestMode(testEnvEnabled)
    commit(SYSTEM_SETTINGS.MUTATIONS.SET_TEST_STATUS, testEnvEnabled)
  },
  async [SYSTEM_SETTINGS.ACTIONS.GET_ASSEMBLY_VERSIONS] ({ dispatch, commit }) {
    const model = {
      url: `${Vue.getAdminHost()}${URL_SYSTEM}/versions?${Vue.helpers.getQueryParamsString()}`
    }
    const resp = await dispatch(AXIOS_REQUESTS.ACTIONS.GET_AXIOS_REQUEST, model, { root: true })
    commit(ERRORS.MUTATIONS.ERROR_CLEAR, null, { root: true })
    commit(SYSTEM_SETTINGS.MUTATIONS.GET_ASSEMBLY_VERSIONS, resp.data)
  },
  async [SYSTEM_SETTINGS.ACTIONS.GET_TEST_STATUS] ({ dispatch, commit }) {
    const model = {
      url: `${Vue.getBusinessHost()}${URL_SYSTEM}/testStatus?${Vue.helpers.getQueryParamsString()}`
    }
    const resp = await dispatch(AXIOS_REQUESTS.ACTIONS.GET_AXIOS_REQUEST, model, { root: true })
    commit(ERRORS.MUTATIONS.ERROR_CLEAR, null, { root: true })
    Vue.changeTestMode(resp.data.testEnvEnabled)
    commit(SYSTEM_SETTINGS.MUTATIONS.GET_TEST_STATUS, resp.data)
  },
  async [SYSTEM_SETTINGS.ACTIONS.CHANGE_LOCALIZATION_STATUS] ({ dispatch, commit }, newStatus) {
    const model = {
      url: `${Vue.getAdminHost()}${URL_SYSTEM}/localizationStatus`,
      model: { localizationEnabled: newStatus }
    }
    await dispatch(AXIOS_REQUESTS.ACTIONS.PUT_AXIOS_REQUEST, model, { root: true })
    commit(ERRORS.MUTATIONS.ERROR_CLEAR, null, { root: true })
    commit(SYSTEM_SETTINGS.MUTATIONS.SET_LOCALIZATION_STATUS, newStatus)
  },
  async [SYSTEM_SETTINGS.ACTIONS.GET_LOCALIZATION_STATUS] ({ dispatch, commit }) {
    const model = {
      url: `${Vue.getBusinessHost()}${URL_SYSTEM}/localizationStatus?${Vue.helpers.getQueryParamsString()}`
    }
    const resp = await dispatch(AXIOS_REQUESTS.ACTIONS.GET_AXIOS_REQUEST, model, { root: true })
    commit(ERRORS.MUTATIONS.ERROR_CLEAR, null, { root: true })
    commit(SYSTEM_SETTINGS.MUTATIONS.GET_LOCALIZATION_STATUS, resp.data)
  },
  async [SYSTEM_SETTINGS.ACTIONS.GET_SERVER_TIME] ({ dispatch, commit }) {
    const model = {
      url: `${Vue.getBusinessHost()}${URL_SYSTEM}/serverTime?${Vue.helpers.getQueryParamsString()}`
    }
    const resp = await dispatch(AXIOS_REQUESTS.ACTIONS.GET_AXIOS_REQUEST, model, { root: true })
    commit(ERRORS.MUTATIONS.ERROR_CLEAR, null, { root: true })
    commit(SYSTEM_SETTINGS.MUTATIONS.GET_SERVER_TIME, resp.data)
  },
  async [SYSTEM_SETTINGS.ACTIONS.CLEAR_SERVER_TIME] ({ commit }) {
    commit(SYSTEM_SETTINGS.MUTATIONS.CLEAR_SERVER_TIME)
  },
  async [SYSTEM_SETTINGS.ACTIONS.SETTINGS_IS_LOADED] ({ commit }) {
    commit(SYSTEM_SETTINGS.MUTATIONS.SETTINGS_IS_LOADED)
  },
  async [SYSTEM_SETTINGS.ACTIONS.CLEAR_ASSEMBLY_VERSIONS] ({ commit }) {
    commit(SYSTEM_SETTINGS.MUTATIONS.CLEAR_ASSEMBLY_VERSIONS)
  },
  async [SYSTEM_SETTINGS.ACTIONS.CLEAR_CONFIGURATION] ({ commit }) {
    commit(SYSTEM_SETTINGS.MUTATIONS.CLEAR_CONFIGURATION)
  },
  async [SYSTEM_SETTINGS.ACTIONS.CLEAR_SMTP_SETTINGS] ({ commit }) {
    commit(SYSTEM_SETTINGS.MUTATIONS.CLEAR_SMTP_SETTING)
  }
}

const mutations = {
  [SYSTEM_SETTINGS.MUTATIONS.GET_CONFIGURATION] (state, model) {
    state[SYSTEM_SETTINGS.STATE.CONFIGURATION].segment = model.segment
    state[SYSTEM_SETTINGS.STATE.CONFIGURATION].themeProps = model.themeProps
    state[SYSTEM_SETTINGS.STATE.CONFIGURATION].loaded = true
  },
  [SYSTEM_SETTINGS.MUTATIONS.CLEAR_CONFIGURATION] (state, model) {
    state[SYSTEM_SETTINGS.STATE.CONFIGURATION].segment = null
    state[SYSTEM_SETTINGS.STATE.CONFIGURATION].themeProps = null
    state[SYSTEM_SETTINGS.STATE.CONFIGURATION].loaded = false
  },
  [SYSTEM_SETTINGS.MUTATIONS.GET_SMTP_SETTING] (state, model) {
    state[SYSTEM_SETTINGS.STATE.SMTP_SETTINGS] = model
  },
  [SYSTEM_SETTINGS.MUTATIONS.CLEAR_SMTP_SETTING] (state) {
    state[SYSTEM_SETTINGS.STATE.SMTP_SETTINGS] = null
  },
  [SYSTEM_SETTINGS.MUTATIONS.GET_TEST_STATUS] (state, model) {
    state[SYSTEM_SETTINGS.STATE.IS_TEST_MODE] = model.testEnvEnabled
  },
  [SYSTEM_SETTINGS.MUTATIONS.GET_ASSEMBLY_VERSIONS] (state, model) {
    state[SYSTEM_SETTINGS.STATE.VERSIONS] = model
  },
  [SYSTEM_SETTINGS.MUTATIONS.GET_SERVER_TIME] (state, serverTime) {
    state[SYSTEM_SETTINGS.STATE.SERVER_TIME] = serverTime
  },
  [SYSTEM_SETTINGS.MUTATIONS.CLEAR_SERVER_TIME] (state) {
    state[SYSTEM_SETTINGS.STATE.SERVER_TIME] = null
  },
  [SYSTEM_SETTINGS.MUTATIONS.SETTINGS_IS_LOADED] (state) {
    state[SYSTEM_SETTINGS.STATE.IS_LOADED] = true
  },
  [SYSTEM_SETTINGS.MUTATIONS.CLEAR_ASSEMBLY_VERSIONS] (state) {
    state[SYSTEM_SETTINGS.STATE.VERSIONS] = null
  },
  [SYSTEM_SETTINGS.MUTATIONS.SET_TEST_STATUS] (state, testEnvEnabled) {
    state[SYSTEM_SETTINGS.STATE.IS_TEST_MODE] = testEnvEnabled
  },
  [SYSTEM_SETTINGS.MUTATIONS.GET_LOCALIZATION_STATUS] (state, model) {
    state[SYSTEM_SETTINGS.STATE.LOCALIZATION_ENABLED] = model.localizationEnabled
  },
  [SYSTEM_SETTINGS.MUTATIONS.SET_LOCALIZATION_STATUS] (state, localizationEnabled) {
    state[SYSTEM_SETTINGS.STATE.LOCALIZATION_ENABLED] = localizationEnabled
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
